import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser } from 'react-icons/fi';
import { useAuth } from '../AuthContext';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [rememberCredentials, setRememberCredentials] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const STORAGE_KEY = 'bpm-remember-login';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setEmail(parsed?.email || '');
        setPassword(parsed?.password || '');
        setRememberCredentials(true);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const previousTheme = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');

    return () => {
      if (previousTheme) {
        document.documentElement.setAttribute('data-theme', previousTheme);
      } else {
        document.documentElement.removeAttribute('data-theme');
      }
    };
  }, []);

  const handleToggleRemember = () => {
    setRememberCredentials((prev) => {
      const next = !prev;
      if (typeof window === 'undefined') {
        return next;
      }
      if (!next) {
        try {
          window.localStorage.removeItem(STORAGE_KEY);
        } catch {
          // ignore
        }
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await login(email, password);
      if (typeof window !== 'undefined' && rememberCredentials) {
        try {
          window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ email, password })
          );
        } catch {
          // ignore
        }
      } else if (typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem(STORAGE_KEY);
        } catch {
          // ignore
        }
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Falha no login');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-slate-950 bg-gradient-to-br from-black via-[#050b1a] to-[#08204a] p-4">
      <div className="flex flex-col items-center gap-4 text-center text-white">
        <div className="flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-full bg-white shadow-xl">
          <FiUser aria-hidden className="text-[2.5rem] text-[#001133]" />
        </div>
        <span className="text-[2.25rem] font-semibold tracking-[0.12em] text-white">
          Área de Acesso
        </span>
      </div>
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/95 p-6 shadow-2xl backdrop-blur"
      >
        <div className="mb-6 flex flex-col items-center text-center">
          <h1 className="text-base font-light tracking-[0.12em] text-slate-900">
            Intelligent BPM Nash Tecnologia
          </h1>
          <p className="mt-2 text-sm text-slate-500">Acesse a plataforma com suas credenciais</p>
        </div>
        {error && <p className="mb-4 text-center text-sm font-medium text-red-500">{error}</p>}
        <div className="mb-4">
          <label className="mb-1 block text-sm font-semibold text-slate-700" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
            placeholder="seu.email@nash.com.br"
            autoComplete="email"
            required
          />
        </div>
        <div className="mb-6">
          <label className="mb-1 block text-sm font-semibold text-slate-700" htmlFor="password">
            Senha
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
            placeholder="Digite sua senha"
            autoComplete="current-password"
            required
          />
        </div>
        <div className="mb-8 flex items-center justify-between text-xs font-medium uppercase tracking-wide text-slate-500">
          <button
            type="button"
            onClick={handleToggleRemember}
            aria-pressed={rememberCredentials}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 transition focus:outline-none focus:ring-2 focus:ring-blue-400/60 ${
              rememberCredentials
                ? 'border-blue-400 bg-blue-50 text-blue-600'
                : 'border-slate-200 bg-white text-slate-500 hover:border-blue-200 hover:text-blue-600'
            }`}
          >
            <span
              className={`h-2.5 w-2.5 rounded-full ${
                rememberCredentials ? 'bg-blue-500' : 'bg-slate-300'
              }`}
            />
            Lembrar usuário e senha
          </button>
        </div>
        <button
          className="w-full rounded-xl bg-gradient-to-r from-blue-700 to-blue-400 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-lg transition hover:from-blue-600 hover:to-blue-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400/60 focus:ring-offset-white"
          type="submit"
        >
          Entrar
        </button>
        <div className="mt-10 flex justify-center">
          <img
            src="/nashlogoazul.png"
            alt="Logo da Nash"
            className="h-24 w-24 drop-shadow-xl"
          />
        </div>
      </form>
    </div>
  );
}
